import { Download, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatDate } from '@/features/common/format';

import { DuplicateCountBadge } from './DuplicateCountBadge';
import { formatBytes, shortenId } from './utils';
import type { ReportResourceView, ReportSearchRow } from './types';

/**
 * One report row (design §9.4). The row is NOT clickable — its actions are the
 * resource downloads below it (no report detail page in P3; the reason is fully
 * visible via the `title`).
 */
export function ReportRow({ row }: { row: ReportSearchRow }) {
  const resolved = row.status === 'resolved';
  const duplicate = Boolean(row.duplicateOf);
  const createdLabel = formatDate(row.createdAt);
  const createdFull = row.createdAt ? new Date(row.createdAt).toString() : undefined;

  return (
    <div className="px-3 py-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Badge
          variant={resolved ? 'outline' : 'secondary'}
          className="w-24 shrink-0 justify-center"
        >
          {resolved ? 'Resolved' : 'Active'}
        </Badge>
        <span
          className="min-w-0 max-w-[calc(100%-10rem)] flex-1 truncate text-sm font-medium"
          title={row.reason}
        >
          {row.reason}
        </span>
        <span className="ml-auto shrink-0 text-sm text-muted-foreground" title={createdFull}>
          {createdLabel}
        </span>
      </div>

      {!duplicate && row.debouncedCounter > 1 ? (
        <div className="mt-1">
          <DuplicateCountBadge debouncedCounter={row.debouncedCounter} />
        </div>
      ) : null}

      {duplicate ? (
        <div className="mt-1 flex items-center gap-2">
          <Badge variant="outline">Duplicate</Badge>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help font-mono text-xs text-muted-foreground">
                of {shortenId(row.duplicateOf as string)}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              This report was folded into an earlier report.
            </TooltipContent>
          </Tooltip>
        </div>
      ) : null}

      {row.resources.length > 0 ? (
        <div className="mt-2 divide-y border-t">
          {row.resources.map((resource) => (
            <ResourceRow key={`${resource.service}:${resource.resourceId}`} resource={resource} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Resource rows (design §9.5): filename + size + a presigned Download only —
 * NEVER any preview or inline content (P5 immutable). Storage resources without
 * a `downloadUrl` (fail-closed presign) render "Download unavailable" instead
 * of a dead button (B3).
 */
export function ResourceRow({ resource }: { resource: ReportResourceView }) {
  const size = formatBytes(resource.size);

  if (resource.service === 'storage') {
    const filename = resource.filename ?? 'Untitled attachment';

    if (resource.downloadUrl) {
      // The whole resource row is the anchor (C1 — ≥40px full-row hit area).
      return (
        <a
          href={resource.downloadUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Download ${filename}`}
          className="flex min-h-10 items-center gap-2 rounded-md pl-6 pr-3 text-sm no-underline hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0 truncate">{filename}</span>
          {size ? <span className="shrink-0 text-muted-foreground">· {size}</span> : null}
          <span
            className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'ml-auto shrink-0')}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Download
          </span>
        </a>
      );
    }

    return (
      <div className="flex min-h-10 items-center gap-2 pl-6 pr-3 text-sm">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 truncate">{filename}</span>
        {size ? <span className="shrink-0 text-muted-foreground">· {size}</span> : null}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="ml-auto shrink-0 cursor-help text-sm text-muted-foreground">
              Download unavailable
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            The server couldn&apos;t authorize this download. Use Refresh and try again, or contact
            an administrator.
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  // Non-storage service: a bare ref, no download affordance (only storage gets presigns).
  return (
    <div className="flex min-h-10 items-center gap-2 pl-6 pr-3 text-sm">
      <span className="font-mono text-xs text-muted-foreground">
        {resource.service}:{resource.resourceId}
      </span>
    </div>
  );
}
